// ===================================================================
// Motivos de eliminação na redação (catálogo por instituição)
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { validateFields } from '../../utils/helpers';
import { useNotifications } from '../../contexts/NotificationContext';

export const EssayEliminationReasonsManager = React.memo(() => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    description: '',
    sort_order: 0,
    is_active: true,
  });
  const [error, setError] = useState('');
  const { addNotification } = useNotifications();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getEssayEliminationReasons(true);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleInputChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (error) setError('');
    },
    [error]
  );

  const handleNew = useCallback(() => {
    setEditingRow(null);
    setFormData({
      code: '',
      title: '',
      description: '',
      sort_order: 0,
      is_active: true,
    });
    setError('');
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingRow(row);
    setFormData({
      code: row.code || '',
      title: row.title || '',
      description: row.description || '',
      sort_order: Number(row.sort_order) || 0,
      is_active: Boolean(row.is_active),
    });
    setError('');
    setShowModal(true);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateFields({ code: formData.code, title: formData.title })) {
        setError('Preencha código e título');
        return;
      }
      try {
        setLoading(true);
        const payload = {
          code: formData.code.trim(),
          title: formData.title.trim(),
          description: (formData.description || '').trim(),
          sort_order: Math.max(-9999, Math.min(99999, parseInt(String(formData.sort_order), 10) || 0)),
          is_active: Boolean(formData.is_active),
        };
        if (editingRow) {
          const { error: updateError } = await apiClient.updateEssayEliminationReason(editingRow.id, payload);
          if (updateError) {
            setError(updateError);
          } else {
            addNotification('Sucesso', 'Motivo atualizado', 'success');
            setShowModal(false);
            setEditingRow(null);
            await load();
          }
        } else {
          const { error: createError } = await apiClient.createEssayEliminationReason(payload);
          if (createError) {
            setError(createError);
          } else {
            addNotification('Sucesso', 'Motivo criado', 'success');
            setShowModal(false);
            await load();
          }
        }
      } catch {
        setError('Erro ao salvar');
      } finally {
        setLoading(false);
      }
    },
    [formData, editingRow, load, addNotification]
  );

  const handleDeactivate = useCallback(
    async (rowId) => {
      if (
        !window.confirm(
          'Desativar este motivo? Ele deixa de aparecer para novos lançamentos (histórico em inscrições antigas permanece).'
        )
      ) {
        return;
      }
      try {
        setLoading(true);
        const { error: delErr } = await apiClient.deleteEssayEliminationReason(rowId);
        if (delErr) {
          setError(delErr);
        } else {
          addNotification('Sucesso', 'Motivo desativado', 'success');
          await load();
        }
      } catch {
        setError('Erro ao desativar');
      } finally {
        setLoading(false);
      }
    },
    [load, addNotification]
  );

  if (loading && rows.length === 0) {
    return <LoadingSpinner text="Carregando motivos…" />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>Motivos de eliminação (redação)</h2>
          <button type="button" onClick={handleNew} style={{ ...styles.button, ...styles.buttonPrimary }}>
            Novo motivo
          </button>
        </div>

        <p style={{ fontSize: '14px', color: '#6B7280', maxWidth: '760px', lineHeight: 1.55, padding: '0 16px' }}>
          Catálogo usado pelo corretor ao registrar desclassificação na prova discursiva. O{' '}
          <strong>código</strong> é um identificador estável (maiúsculas, números e sublinhado); as inscrições guardam os{' '}
          <strong>IDs</strong> dos motivos escolhidos. A lista atualiza ao abrir a tela e após criar, editar ou desativar.
        </p>

        {error && <Alert message={error} type="error" onClose={() => setError('')} />}

        {rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            <h3 style={{ marginBottom: 8 }}>Nenhum motivo cadastrado</h3>
            <p>Use <strong>Novo motivo</strong> para incluir itens nesta instituição.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', padding: '0 16px 16px' }}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableHeaderCell}>Ordem</th>
                  <th style={styles.tableHeaderCell}>Código</th>
                  <th style={styles.tableHeaderCell}>Título</th>
                  <th style={styles.tableHeaderCell}>Descrição</th>
                  <th style={styles.tableHeaderCell}>Ativo</th>
                  <th style={styles.tableHeaderCell}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={styles.tableRow}>
                    <td style={styles.tableCell}>{r.sort_order}</td>
                    <td style={styles.tableCell}>
                      <code>{r.code}</code>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{ fontWeight: 600, color: '#1F2937' }}>{r.title}</span>
                    </td>
                    <td style={styles.tableCell}>
                      <div style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.description || '—'}
                      </div>
                    </td>
                    <td style={styles.tableCell}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          ...(r.is_active ? styles.statusApproved : styles.statusRejected),
                        }}
                      >
                        {r.is_active ? 'Sim' : 'Não'}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => handleEdit(r)}
                          style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeactivate(r.id)}
                          disabled={!r.is_active}
                          style={{
                            ...styles.button,
                            ...styles.buttonDanger,
                            ...styles.buttonSmall,
                            opacity: r.is_active ? 1 : 0.45,
                          }}
                        >
                          Desativar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingRow(null);
        }}
        title={editingRow ? 'Editar motivo de eliminação' : 'Novo motivo de eliminação'}
      >
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Código *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                style={styles.input}
                placeholder="Ex.: MOTIVO_CUSTOM_01"
                required
                disabled={Boolean(editingRow?.is_system_default)}
              />
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: 4 }}>
                {editingRow?.is_system_default
                  ? 'Código dos itens do catálogo sugerido não pode ser alterado.'
                  : 'Será normalizado para maiúsculas (apenas letras, números e _).'}
              </div>
            </div>
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Título *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                style={styles.input}
                required
              />
            </div>
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Descrição</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                style={styles.textarea}
                rows={4}
              />
            </div>
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Ordem de exibição</label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => handleInputChange('sort_order', e.target.value)}
                style={{ ...styles.input, maxWidth: 120 }}
              />
            </div>
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  style={{ marginRight: 8 }}
                />
                Motivo ativo
              </label>
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: 4 }}>
                Inativos não aparecem para o corretor ao lançar nota.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                setEditingRow(null);
              }}
              style={{ ...styles.button, ...styles.buttonSecondary }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{ ...styles.button, ...styles.buttonPrimary, opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Salvando…' : editingRow ? 'Atualizar' : 'Criar'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
});
